import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Divider, LinearProgress, Typography, useTheme } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { getSharedFormStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import { handleLoanFeesError } from 'app/pages/loanApplication/LoanApplication.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import Skeleton from 'app/shared/components/common/Skeleton';
import {
  ChangeEventReason,
  onAutocompleteChangeInForm,
} from 'app/shared/components/form/Autocomplete';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import withPreparedData from 'hocs/withPreparedData';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';
import usePrevious from 'hooks/usePrevious';
import { getChangedFieldsByConfig, getConfigInfo } from 'utils/loanManagement';

import { closeDialogAction, showMessageAction } from 'store/global/actions';
import {
  editLoanCicFeeReasonsAction,
  getLoanCicFeeReasonsAction,
  getLoanFeesAction,
} from 'store/loanApplications/actions';
import {
  selectCicFeeReasonIdsByGroups,
  selectCicFeeReasonOptionsIncludesDeleted,
  selectEntityGroupChangedPropsByIds,
  selectExtendedLoanFeesValues,
  selectLoanCicFeeReasonOptions,
} from 'store/loanApplications/selectors';
import { getCicFeeReasonsFilteredAction as getAllCicFeeReasonsAction } from 'store/system/actions';
import { selectFeeTypes } from 'store/system/selectors';

import { LoanFeesTestIds } from '../../LoanFees.testIds';

import { getLoanFeeCicFormConfig } from './LoanFeeCicForm.config';
import { getStyles } from './LoanFeeCicForm.styles';
import {
  getAmountNotChangedErrors,
  getCicFeeReasonsInvalidError,
  getLoanFeeReasonsOutputData,
  getOverriddenFeeIdPostfix,
  mergeLoanFeesCic,
  transformLoanFeesCicForm,
} from './LoanFeeCicForm.utils';

const LoanFeeCicForm = ({
  isLoading,
  setIsLoading,
  formRef,
  cicFeeReasonOptions,
  cicFeeReasons,
  loanFees,
  allFees,
  dataIds,
}) => {
  const dispatch = useDispatch();

  const formCicFeeReasonsRef = useRef();

  const loanId = useLoanId();
  const { loanCicFeesIds, cicFeeReasonsIds, cicFeeReasonsIdsToReselect } = dataIds;

  const theme = useTheme();
  const classes = getStyles(theme);
  const sharedClasses = useMemo(() => getSharedFormStyles(theme), [theme]);

  const [loanCicFeesIdsState, setLoanCicFeesIdsState] = useState(loanCicFeesIds);
  const [cicFeeReasonReselectedIds, setCicFeeReasonReselectedIds] = useState(
    cicFeeReasonsIdsToReselect,
  );

  const loanCicFeesPendingChangesData = useSelector(
    selectEntityGroupChangedPropsByIds({
      partialIds: loanCicFeesIdsState,
      idPostfix: getOverriddenFeeIdPostfix(loanId),
    }),
  );
  const loanCicFeeReasonsPendingChangesData = useSelector(
    selectEntityGroupChangedPropsByIds({ idPrefix: loanId, partialIds: cicFeeReasonsIds }),
  );

  const { initialFormValues, schema, fieldsConfig, initialConfig } = useMemo(() => {
    const cicFeeReasonsValues = cicFeeReasons?.map(({ value }) => value);
    const loanCicFees = mergeLoanFeesCic({
      isInitialMerge: true,
      cicFeeReasons,
      loanFees,
      allFees,
    });

    return getConfigInfo({
      applicationData: { cicFeeReasons: cicFeeReasonsValues, loanCicFees },
      config: getLoanFeeCicFormConfig({
        loanCicFees,
        cicFeeReasonOptions,
        loanFees,
      }),
    });
  }, [cicFeeReasonOptions, loanFees, allFees, cicFeeReasons]);

  const { changedFields } = useMemo(() => {
    const isCicFeeReasonsChanged = !!loanCicFeeReasonsPendingChangesData.filter(Boolean).length;

    return getChangedFieldsByConfig({
      initialConfig,
      pendingChangesData: {
        loanCicFees: loanCicFeesPendingChangesData,
        cicFeeReasons: { initialValue: false, newValue: isCicFeeReasonsChanged },
      },
    });
  }, [loanCicFeesPendingChangesData, loanCicFeeReasonsPendingChangesData, initialConfig]);

  const {
    form,
    handleChange,
    validationErrors,
    setValidationErrors,
    setSchema,
    setInForm,
    setForm,
    validateField,
    validateForm,
    isFormChanged,
  } = useForm(initialFormValues, fieldsConfig);

  const cicFeeReasonsCount = form.cicFeeReasons?.length;
  const cicFeeReasonsCountPrevious = usePrevious(cicFeeReasonsCount);
  const isCicFeeReasonsChanged = isFormChanged && cicFeeReasonsCount !== cicFeeReasonsCountPrevious;
  formCicFeeReasonsRef.current = form.cicFeeReasons || [];

  useEffect(() => {
    setSchema(schema);
  }, [schema, setSchema]);

  const loanCicFees = useMemo(
    () => {
      const transformedForm = transformLoanFeesCicForm(form);
      const loanCicFees = _.keyBy(transformedForm.loanCicFees, ({ feeTypeId }) => feeTypeId);

      const allCicFeeReasonsValues = cicFeeReasonOptions.map(({ value }) => value);
      const cicFeeReasons = form.cicFeeReasons?.sort(
        (a, b) => allCicFeeReasonsValues.indexOf(a.value) - allCicFeeReasonsValues.indexOf(b.value),
      );

      return mergeLoanFeesCic({
        cicFeeReasons,
        loanCicFees,
        loanFees,
        allFees,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCicFeeReasonsChanged],
  );

  const isCicFeeReasonReselectedIdsChanged = useMemo(
    () => !_.isEqual(cicFeeReasonsIdsToReselect, cicFeeReasonReselectedIds),
    [cicFeeReasonsIdsToReselect, cicFeeReasonReselectedIds],
  );

  useEffect(() => {
    if (isCicFeeReasonsChanged) {
      const cicFeeReasons = form.cicFeeReasons?.map(({ value }) => value);

      const configInfo = getConfigInfo({
        applicationData: { cicFeeReasons, loanCicFees },
        config: formGeneratorConfig,
      });

      const loanCicFeesIds = loanCicFees.map(({ feeTypeId }) => feeTypeId);

      setLoanCicFeesIdsState(loanCicFeesIds);

      setValidationErrors({});
      setForm(configInfo.initialFormValues);
      setSchema(configInfo.schema);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCicFeeReasonsChanged, loanCicFees]);

  const onChangeCicFeeReason = useCallback(
    (e, options, reason) => {
      if (reason === ChangeEventReason.REMOVE_OPTION) {
        const [{ value } = {}] = _.differenceBy(formCicFeeReasonsRef.current, options);

        setCicFeeReasonReselectedIds(prevState => _.omit(prevState, value));
      }

      onAutocompleteChangeInForm({ validateField, setInForm })(e, options);
    },
    [validateField, setInForm],
  );

  const submitForm = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }

    const amountNotChangedErrors = getAmountNotChangedErrors({
      form,
      loanFees,
      pendingChangesData: loanCicFeesPendingChangesData,
    });
    const cicFeeReasonsInvalidError = getCicFeeReasonsInvalidError(cicFeeReasonReselectedIds);

    const isValidationErrorsOccurred = [amountNotChangedErrors, cicFeeReasonsInvalidError].some(
      validationError => !_.isEmpty(validationError),
    );

    if (isValidationErrorsOccurred) {
      return setValidationErrors({
        ...amountNotChangedErrors,
        ...cicFeeReasonsInvalidError,
      });
    }

    const successMessage = 'Fee CIC has been edited and is ready to be stored in the loan record';

    const actionData = {
      cicFeeReasons: getLoanFeeReasonsOutputData({ formValues: form, initialFormValues }),
      loanId,
      requestMetadata: { tabId: LoanApplicationTabId.LOAN_FEES },
    };

    setIsLoading(true);

    dispatch(editLoanCicFeeReasonsAction(actionData))
      .then(() => {
        dispatch(showMessageAction({ message: successMessage, variant: 'success' }));
        dispatch(closeDialogAction());

        return dispatch(getLoanFeesAction({ loanId }));
      })
      .catch(({ response }) =>
        handleLoanFeesError({ response, dispatch, onRetryAction: submitForm }),
      )
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    loanId,
    form,
    initialFormValues,
    cicFeeReasonReselectedIds,
    loanFees,
    dispatch,
    validateForm,
    setValidationErrors,
    setIsLoading,
    loanCicFeesPendingChangesData,
  ]);

  const onSubmitLoanCicFees = useCallback(
    e => {
      e.preventDefault();

      submitForm();
    },
    [submitForm],
  );

  const formGeneratorConfig = useMemo(
    () =>
      getLoanFeeCicFormConfig({
        loanCicFees,
        cicFeeReasonOptions,
        loanFees,
        onChangeCicFeeReason,
        cicFeeReasonReselectedIds,
      }),
    [loanCicFees, cicFeeReasonOptions, loanFees, onChangeCicFeeReason, cicFeeReasonReselectedIds],
  );

  const isSubmitDisabled = !(isFormChanged || isCicFeeReasonReselectedIdsChanged) || isLoading;

  return (
    <>
      <LinearProgress className={isLoading ? 'visible' : 'invisible'} color="primary" />
      <form
        noValidate
        className={clsx(
          sharedClasses.root,
          classes.override,
          'flex flex-col relative w-xl min-h-600 p-24',
        )}
        data-ischanged={isFormChanged}
        data-testid={LoanFeesTestIds.LOAN_CIC_FEES_FORM}
        ref={formRef}
        onSubmit={onSubmitLoanCicFees}
      >
        <Button
          className={clsx(sharedClasses.submitButton, 'self-end order-first')}
          color="primary"
          data-testid={LoanFeesTestIds.LOAN_CIC_FEES_FORM_SUBMIT_BUTTON}
          disabled={isSubmitDisabled}
          type="submit"
          variant="contained"
        >
          Done
        </Button>

        <Box className="uppercase text-14 relative flex" sx={classes.header}>
          <Typography variant="regular14">Fee CIC</Typography>
          <Typography variant="regular14">Amount</Typography>
        </Box>

        <Divider className="mb-24" />

        <FormGenerator
          changedFields={changedFields}
          config={formGeneratorConfig}
          form={form}
          formName={FormNames.FEES}
          handleChange={handleChange}
          isLoading={isLoading}
          numberOfColumns={3}
          setInForm={setInForm}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      </form>
    </>
  );
};

LoanFeeCicForm.propTypes = {
  ...WithLoadingPropTypes,
  cicFeeReasonOptions: PropTypes.array.isRequired,
  cicFeeReasons: PropTypes.array.isRequired,
  loanFees: PropTypes.object.isRequired,
  allFees: PropTypes.object.isRequired,
  dataIds: PropTypes.object.isRequired,
  formRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
};

export default compose(
  withLoading,
  withPreparedData({
    fetchData: [getLoanCicFeeReasonsAction, getAllCicFeeReasonsAction],
    dataSelectors: {
      cicFeeReasonOptions: selectCicFeeReasonOptionsIncludesDeleted,
      cicFeeReasons: selectLoanCicFeeReasonOptions,
      loanFees: selectExtendedLoanFeesValues,
      allFees: selectFeeTypes,
      dataIds: selectCicFeeReasonIdsByGroups,
    },
    skeletonComponent: (
      <div className="overflow-x-hidden">
        <Skeleton width={960} />
      </div>
    ),
  }),
)(LoanFeeCicForm);
